// SPDX-License-Identifier: AGPL-3.0-or-later

import type {TaskStatus} from '@app/features/tasks/state/Tasks';

/** The parts of a Trello board export (Board menu → Print, export and share → Export as JSON) we read. */
interface TrelloExport {
	lists?: Array<{id: string; name: string; closed?: boolean}>;
	cards?: Array<{
		id: string;
		name: string;
		desc?: string;
		idList: string;
		closed?: boolean;
		due?: string | null;
		dueComplete?: boolean;
		idMembers?: Array<string>;
		labels?: Array<{name?: string; color?: string}>;
		shortUrl?: string;
		url?: string;
	}>;
	members?: Array<{id: string; fullName?: string; username?: string}>;
}

export interface ImportedCard {
	title: string;
	notes: string | null;
	status: TaskStatus;
	dueDate: string | null;
	/** Names of the Trello members on the card, matched to people here by name. */
	memberNames: Array<string>;
	/** Written into the notes; used to skip cards that were already imported. */
	sourceUrl: string | null;
}

const DONE_LIST = /\b(done|complete|completed|finished|shipped|closed)\b/i;
const QA_LIST = /\b(qa|review|testing|test|verify)\b/i;
const INPUT_LIST = /\b(input|waiting|blocked|question|needs?)\b/i;
const DOING_LIST = /\b(doing|in progress|progress|wip)\b/i;

/** Matches Trello list names to the board's columns: Backlog, Input needed, In progress, QA, Done. */
export function statusForList(listName: string, dueComplete: boolean): TaskStatus {
	if (dueComplete || DONE_LIST.test(listName)) return 'done';
	if (QA_LIST.test(listName)) return 'qa';
	if (INPUT_LIST.test(listName)) return 'input';
	if (DOING_LIST.test(listName)) return 'doing';
	return 'todo';
}

const TITLE_MAX = 300;
const NOTES_MAX = 4000;

/** Turns a Trello board export into to-dos. Archived cards and archived lists are left out. */
export function parseTrelloExport(raw: unknown): Array<ImportedCard> {
	if (raw == null || typeof raw !== 'object') throw new Error('Not a Trello export');
	const board = raw as TrelloExport;
	if (!Array.isArray(board.cards) || !Array.isArray(board.lists)) throw new Error('Not a Trello export');
	const lists = new Map(board.lists.map((list) => [list.id, list]));
	const members = new Map((board.members ?? []).map((m) => [m.id, m.fullName || m.username || '']));
	const out: Array<ImportedCard> = [];
	for (const card of board.cards) {
		const list = lists.get(card.idList);
		if (card.closed || !list || list.closed) continue;
		const name = card.name.trim();
		if (!name) continue;
		let title = name;
		let overflow = '';
		if (name.length > TITLE_MAX) {
			title = `${name.slice(0, TITLE_MAX - 1).trimEnd()}…`;
			overflow = name;
		}
		const sourceUrl = card.shortUrl || card.url || null;
		const labels = (card.labels ?? []).map((l) => l.name?.trim()).filter((l): l is string => !!l);
		const parts = [
			overflow,
			card.desc?.trim() ?? '',
			labels.length ? `Labels: ${labels.join(', ')}` : '',
		].filter(Boolean);
		// Only what people wrote: the Trello list and link are noise on the board.
		let notes = parts.join('\n\n');
		if (notes.length > NOTES_MAX) notes = `${notes.slice(0, NOTES_MAX - 1).trimEnd()}…`;
		out.push({
			title,
			notes: notes || null,
			status: statusForList(list.name, card.dueComplete === true),
			dueDate: card.due ? card.due.slice(0, 10) : null,
			memberNames: (card.idMembers ?? []).map((id) => members.get(id) ?? '').filter(Boolean),
			sourceUrl,
		});
	}
	return out;
}

/** Finds the person here whose name best matches a Trello member's full name. */
export function matchPerson(
	trelloName: string,
	people: ReadonlyArray<{id: string; names: ReadonlyArray<string>}>,
): string | null {
	const norm = (value: string) =>
		value
			.toLowerCase()
			.normalize('NFKD')
			.replace(/[^\p{L}\p{N} ]/gu, '')
			.trim();
	const full = norm(trelloName);
	const first = full.split(/\s+/)[0] ?? '';
	if (!full) return null;
	const exact = people.find((p) => p.names.some((n) => norm(n) === full));
	if (exact) return exact.id;
	const byFirst = people.filter((p) => p.names.some((n) => norm(n).split(/\s+/)[0] === first));
	return byFirst.length === 1 ? byFirst[0].id : null;
}
